// Client-side auth: context + provider, useAuth hook, RequireRole route guard,
// and a fetch wrapper that auto-attaches the Authorization header.
//
// Token lives in localStorage as 'hotelops-token'. The provider validates it
// on mount by calling /api/me and pulls the canonical user record.

import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { Navigate, useLocation } from 'react-router-dom';

const TOKEN_KEY = 'hotelops-token';
const API       = process.env.REACT_APP_API_URL || '/api';

// ── apiFetch ────────────────────────────────────────────────────────────────
// Thin wrapper. Returns { ok, status, data }. Body is whatever the caller
// passes (we set Content-Type to JSON when a body is present).

export const apiFetch = async (path, opts = {}) => {
  const token   = localStorage.getItem(TOKEN_KEY);
  const headers = {
    ...(opts.headers || {}),
    ...(opts.body ? { 'Content-Type': 'application/json' } : {}),
    ...(token     ? { Authorization: `Bearer ${token}` }   : {}),
  };

  if (process.env.NODE_ENV === 'development') {
    // Single-line trace so the network panel and the console agree.
    // eslint-disable-next-line no-console
    console.debug(
      `[apiFetch] ${opts.method || 'GET'} ${path}`,
      'token:', token ? `${token.slice(0, 12)}…` : 'NONE'
    );
  }

  let res, data = null;
  try {
    res = await fetch(`${API}${path}`, { ...opts, headers });
  } catch {
    return { ok: false, status: 0, data: { message: 'Cannot reach server' } };
  }
  try { data = await res.json(); } catch { /* non-JSON */ }

  // 401 means the token is missing/invalid/expired. Clear it and notify
  // anything listening (AuthProvider) so the user gets a clean kick to
  // /login instead of a silent dashboard with cryptic errors.
  if (res.status === 401) {
    if (token) localStorage.removeItem(TOKEN_KEY);
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('auth:expired', {
        detail: { path, hadToken: !!token },
      }));
    }
  }

  return { ok: res.ok, status: res.status, data };
};

// ── authFetch ───────────────────────────────────────────────────────────────
// Sprint 19.4: drop-in replacement for the global `fetch` for the many call
// sites that were written as `fetch('/api/…').then(r => r.json())`. Same
// signature, returns the real Response — but attaches the Bearer token
// (the /api/admin/* routes are admin-only now) and routes '/api/…' through
// the configured API base so the GitHub-Pages build (REACT_APP_API_URL set)
// reaches the server too. 401 handling matches apiFetch.
export const authFetch = async (url, opts = {}) => {
  const token   = localStorage.getItem(TOKEN_KEY);
  const headers = {
    ...(opts.headers || {}),
    ...(opts.body && !(opts.headers || {})['Content-Type'] ? { 'Content-Type': 'application/json' } : {}),
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
  const target = typeof url === 'string' && url.startsWith('/api/') ? `${API}${url.slice(4)}` : url;
  const res = await fetch(target, { ...opts, headers });
  if (res.status === 401) {
    if (token) localStorage.removeItem(TOKEN_KEY);
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('auth:expired', {
        detail: { path: url, hadToken: !!token },
      }));
    }
  }
  return res;
};

// ── Staff entry (Sprint 24) ─────────────────────────────────────────────────
// Two doors for non-admin users, same credentials:
//   'clockin' — the original kiosk dashboard at /:tenant/clockin (clock in/out, timesheet, calendar; short idle sign-out)
//   'staff'   — the staff portal at /:tenant/staff (Home with forecast tiles, Housekeeping for the front desk, …)
// The login screen the person used decides which one they get, and it is remembered so a refresh / revisit lands in the same place.
const STAFF_ENTRY_KEY = 'hotelops-staff-entry';
export const getStaffEntry = () => {
  try { return localStorage.getItem(STAFF_ENTRY_KEY) === 'staff' ? 'staff' : 'clockin'; } catch { return 'clockin'; }
};
export const setStaffEntry = (entry) => {
  try { localStorage.setItem(STAFF_ENTRY_KEY, entry === 'staff' ? 'staff' : 'clockin'); } catch { /* storage unavailable */ }
};
/** Sprint 24.1 / 24.2: the front-desk features (forecast tiles on Home, Housekeeping) belong to staff whose ROLE is "front desk" or whose
 *  DEPARTMENT is named "Front Desk" (the server checks the same two things from the token). Managers/dev use the admin shell instead. */
export const isFrontDesk = (user) => !!user && (user.role === 'front_desk' || (typeof user.department === 'string' && /^front[\s_-]*desk$/i.test(user.department.trim())));
/** Where a signed-in (non-dev) user belongs for this property. */
export const homePathFor = (user, slug) =>
  (user && user.role === 'admin') ? `/${slug}/admin` : `/${slug}/${getStaffEntry() === 'staff' ? 'staff' : 'clockin'}`;

// ── Context ─────────────────────────────────────────────────────────────────

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user,    setUser]    = useState(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    const token = localStorage.getItem(TOKEN_KEY);
    if (!token) {
      setUser(null);
      setLoading(false);
      return;
    }
    const { ok, data } = await apiFetch('/me');
    if (ok && data?.success) {
      setUser(data.user);
    } else {
      localStorage.removeItem(TOKEN_KEY);
      setUser(null);
    }
    setLoading(false);
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  // If any apiFetch elsewhere hits 401 (stale/missing/expired token), it
  // dispatches 'auth:expired'. We clear local user state so RequireRole
  // bounces the next render to /login rather than letting components show
  // empty dashboards with cryptic "missing token" banners.
  useEffect(() => {
    const onExpired = () => setUser(null);
    window.addEventListener('auth:expired', onExpired);
    return () => window.removeEventListener('auth:expired', onExpired);
  }, []);

  const loginStaff = async (identifier, pin) => {
    const { ok, data } = await apiFetch('/auth/staff/login', {
      method: 'POST',
      body:   JSON.stringify({ identifier, pin: pin || undefined }),
    });
    if (!ok || !data?.success) {
      return {
        success: false,
        message: data?.message || 'Login failed',
        pin_required: !!data?.pin_required,
      };
    }
    localStorage.setItem(TOKEN_KEY, data.token);
    setUser(data.user);
    return { success: true, user: data.user };
  };

  const loginAdmin = async (username, password) => {
    const { ok, data } = await apiFetch('/auth/admin/login', {
      method: 'POST',
      body:   JSON.stringify({ username, password }),
    });
    if (!ok || !data?.success) {
      return { success: false, message: data?.message || 'Login failed' };
    }
    localStorage.setItem(TOKEN_KEY, data.token);
    setUser(data.user);
    return { success: true, user: data.user };
  };

  // Sprint 22.4: server-verified dev sign-in (replaces the client-side dev/dev gate). Same token slot as other sessions.
  const loginDev = async (username, password) => {
    const { ok, status, data } = await apiFetch('/auth/dev/login', {
      method: 'POST',
      body:   JSON.stringify({ username, password }),
    });
    if (!ok || !data?.success) {
      return { success: false, status, message: data?.message || 'Login failed' };
    }
    localStorage.setItem(TOKEN_KEY, data.token);
    setUser(data.user);
    return { success: true, user: data.user };
  };

  const logout = async () => {
    try { await apiFetch('/auth/logout', { method: 'POST' }); } catch {}
    localStorage.removeItem(TOKEN_KEY);
    // Sprint 16.8: drop every session-scoped key that's keyed by
    // the current user. Without this, surfaces that gate on
    // "first landing post-login" (notably the Sprint-16.1
    // FocusedAction "dismissed" flag) inherit the previous
    // session's state and either flash + disappear (focused
    // bug the GM reported) or never re-appear. List is short;
    // we'll add to it as more first-landing flows ship.
    try {
      sessionStorage.removeItem('hotelops-staff-focused-dismissed');
    } catch { /* sessionStorage unavailable — ignore */ }
    setUser(null);
  };

  const setPin = async (pin) => {
    const { ok, data } = await apiFetch('/auth/staff/set-pin', {
      method: 'POST',
      body:   JSON.stringify({ pin }),
    });
    if (ok && data?.success) {
      await refresh(); // clears pin_must_set
      return { success: true };
    }
    return { success: false, message: data?.message || 'Could not set PIN' };
  };

  const changePin = async (currentPin, newPin) => {
    const { ok, data } = await apiFetch('/auth/staff/change-pin', {
      method: 'POST',
      body:   JSON.stringify({ currentPin, newPin }),
    });
    if (ok && data?.success) {
      await refresh();
      return { success: true };
    }
    return { success: false, message: data?.message || 'Could not change PIN' };
  };

  const value = { user, loading, loginStaff, loginAdmin, loginDev, logout, setPin, changePin, refresh };
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside <AuthProvider>');
  return ctx;
};

// ── RequireRole ─────────────────────────────────────────────────────────────
// Usage:
//   <Route element={<RequireRole role="staff" />}> ... </Route>
//   <Route element={<RequireRole role="admin" />}> ... </Route>
//
// Accepts a single role string:
//   "staff" — any non-admin authed user (employee | front_desk)
//   "admin" — admin only
//   "any"   — just authed

export const RequireRole = ({ role = 'any', children }) => {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) return null;

  if (!user) {
    // Sprint 11.2: both roles funnel to `/` (the picker). After the
    // user picks a property + signs in, the per-tenant login page
    // routes them to the right post-auth destination, and the
    // `from` state below preserves the originally-requested URL
    // across the redirect for the standard "you tried to deep-link,
    // sign in then we'll take you there" flow.
    return <Navigate to="/" replace state={{ from: location }} />;
  }

  // Sprint 22.6: a dev session uses the SAME admin shell as managers (plus its Dev tools group); it never opens the staff shell.
  if (user.role === 'dev' && role === 'staff') return <Navigate to="/dev" replace />;
  const isAdmin = user.role === 'admin';
  const ok =
    role === 'any' ||
    (role === 'dev' && user.role === 'dev') ||
    (role === 'admin' && (isAdmin || user.role === 'dev')) ||
    (role === 'staff' && !isAdmin && user.role !== 'dev');

  if (!ok) {
    // Sprint 11.2.1: wrong role for this surface — send them to
    // their own shell. Per-tenant slug comes from localStorage
    // (set on login); fall back to `/` if missing.
    const slug = typeof window !== 'undefined'
      ? localStorage.getItem('hotelops-tenant-slug')
      : null;
    if (slug) {
      return <Navigate to={homePathFor(user, slug)} replace />;
    }
    return <Navigate to="/" replace />;
  }

  return children;
};

// ── RedirectIfAuthed ────────────────────────────────────────────────────────
// Wraps a login page so already-authed users don't see it.

// Sprint 22.4: the /dev surface — anyone who is not a signed-in dev goes to the dev sign-in page.
export const RequireDev = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (!user || user.role !== 'dev') return <Navigate to="/login/dev" replace />;
  return children;
};

export const RedirectIfAuthed = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (user && user.role === 'dev') return <Navigate to="/dev" replace />;
  if (user) {
    // Sprint 11.2.1: authed users on the login page get bounced to
    // their per-tenant shell.
    const slug = typeof window !== 'undefined'
      ? localStorage.getItem('hotelops-tenant-slug')
      : null;
    if (slug) {
      return <Navigate to={homePathFor(user, slug)} replace />;
    }
    return <Navigate to="/" replace />;
  }
  return children;
};
